import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Copy, Trash2, RefreshCw, KeyRound, Terminal, CheckCircle, Loader2 } from 'lucide-react';

/**
 * UI for managing long-lived "sync keys" used by the Mac launchd background
 * sync job. Each key is shown ONCE at creation; thereafter only its prefix +
 * suffix + last-used timestamp are visible.
 *
 * Props:
 *   apiBase    string — usually "https://operatr.me" (so the install command
 *              points at the production URL even when previewing locally)
 *   authToken  string — current JWT, used to call the management endpoints
 */
const PermanentSyncKeyPanel = ({ apiBase, authToken }) => {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newKeyPlaintext, setNewKeyPlaintext] = useState(''); // shown ONCE after issue
  const [newKeyLabel, setNewKeyLabel] = useState('My Mac');

  const headers = authToken
    ? { 'Authorization': `Bearer ${authToken}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };

  const loadKeys = useCallback(async () => {
    if (!authToken) return;
    setLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/auth/calendar/sync-keys`, { headers });
      if (res.ok) {
        const data = await res.json();
        setKeys(data.keys || []);
      } else {
        toast.error(`Failed to load sync keys (HTTP ${res.status})`);
      }
    } catch (err) {
      toast.error(`Failed to load sync keys: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [apiBase, authToken]);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const issueKey = async () => {
    if (!authToken) {
      toast.error('Not signed in.');
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${apiBase}/api/auth/calendar/sync-keys`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ label: newKeyLabel || 'My Mac' }),
      });
      const data = await res.json();
      if (res.ok && data.plaintext_key) {
        setNewKeyPlaintext(data.plaintext_key);
        toast.success('Sync key created — copy it now (it cannot be shown again).');
        loadKeys();
      } else {
        toast.error(data.detail || 'Failed to create sync key.');
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (keyId) => {
    if (!window.confirm('Revoke this sync key? The Mac using it will stop syncing immediately.')) return;
    try {
      const res = await fetch(`${apiBase}/api/auth/calendar/sync-keys/${keyId}`, {
        method: 'DELETE',
        headers,
      });
      if (res.ok) {
        toast.success('Key revoked.');
        loadKeys();
      } else {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || `Revoke failed (HTTP ${res.status})`);
      }
    } catch (err) {
      toast.error(err.message);
    }
  };

  const copyText = (text, msg = 'Copied!') => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => toast.success(msg)).catch(() => toast.error('Copy failed'));
  };

  // Two install commands:
  //  - "prefix" version embeds the just-issued key into the command directly,
  //    so the user doesn't have to paste it twice (and it works on first
  //    try even when piped via `curl | bash`). Available only right after a
  //    new key is generated.
  //  - "generic" version is shown otherwise and prompts via /dev/tty.
  const installCommandGeneric = `curl -fsSL ${apiBase}/api/auth/calendar/install-script.sh | bash`;
  // Correct env-var placement: put it before `bash`, NOT before `curl` —
  // otherwise the variable is set on curl (which doesn't read it) instead of
  // bash (which does).
  const installCommandWithKey = newKeyPlaintext
    ? `curl -fsSL ${apiBase}/api/auth/calendar/install-script.sh | OPERATR_SYNC_KEY=${newKeyPlaintext} bash`
    : null;

  return (
    <div className="space-y-4" data-testid="sync-key-panel">
      {/* Header */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound size={18} className="text-amber-400" />
          <h4 className="text-white font-semibold">Mac Sync Agent — Permanent Key</h4>
        </div>
        <p className="text-xs text-gray-400">
          Generate a key once, save it on your Mac, and the sync runs in the background every 15 minutes — forever, no expiring tokens.
        </p>
      </div>

      {/* Step 1: Create a key (or show the freshly-issued one) */}
      {newKeyPlaintext ? (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-2">
            <CheckCircle size={18} className="text-amber-300" />
            <h4 className="text-amber-200 font-semibold">Your new sync key — copy it NOW</h4>
          </div>
          <p className="text-xs text-amber-200/80 mb-3">
            This is the only time you'll see this key. The next steps will tell you what to do with it.
          </p>
          <div className="flex items-stretch gap-2">
            <code
              data-testid="new-sync-key-value"
              className="flex-1 px-3 py-2 bg-black/70 border border-amber-500/40 rounded-md text-amber-200 text-sm font-mono select-all break-all"
            >
              {newKeyPlaintext}
            </code>
            <button
              onClick={() => copyText(newKeyPlaintext, 'Sync key copied to clipboard.')}
              className="px-3 py-2 bg-amber-500 hover:bg-amber-400 text-black text-sm font-semibold rounded-md flex items-center gap-1.5"
              data-testid="copy-new-sync-key-btn"
            >
              <Copy size={14} /> Copy
            </button>
          </div>
          <button
            onClick={() => setNewKeyPlaintext('')}
            className="mt-3 text-xs text-amber-300/80 hover:text-amber-200 underline"
          >
            I've saved it — hide
          </button>
        </div>
      ) : (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
          <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
            <span className="bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded">1</span>
            Generate your sync key
          </h4>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="text"
              value={newKeyLabel}
              onChange={(e) => setNewKeyLabel(e.target.value)}
              placeholder="Device label (e.g. Mac Mini)"
              className="flex-1 min-w-[180px] px-3 py-2 bg-black/60 border border-gray-600 rounded-md text-white text-sm focus:border-purple-500 focus:outline-none"
              data-testid="new-sync-key-label"
            />
            <button
              onClick={issueKey}
              disabled={creating || !authToken}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 disabled:bg-gray-700 disabled:text-gray-500 text-white text-sm font-semibold rounded-md flex items-center gap-1.5"
              data-testid="issue-sync-key-btn"
            >
              {creating ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
              Generate key
            </button>
          </div>
          {!authToken && (
            <p className="text-xs text-red-400 mt-2">Sign in first — keys are tied to your account.</p>
          )}
        </div>
      )}

      {/* Step 2: One-line installer */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
        <h4 className="text-white font-semibold mb-2 flex items-center gap-2">
          <span className="bg-purple-500 text-white text-xs px-1.5 py-0.5 rounded">2</span>
          Install the sync agent on your Mac (one-time)
        </h4>

        {installCommandWithKey ? (
          <>
            <p className="text-xs text-gray-400 mb-2">
              <span className="text-amber-300 font-semibold">Easiest:</span> this command has your new key already baked in — open Terminal on your Mac, paste, hit Enter. No prompt to fill in.
            </p>
            <div className="flex items-stretch gap-2">
              <code
                data-testid="install-command-with-key"
                className="flex-1 px-3 py-2 bg-black/70 border border-amber-500/30 rounded-md text-amber-100 text-[11px] font-mono select-all break-all"
              >
                {installCommandWithKey}
              </code>
              <button
                onClick={() => copyText(installCommandWithKey, 'Install command (with key) copied!')}
                className="px-3 py-2 bg-amber-500 hover:bg-amber-400 text-black text-xs font-semibold rounded-md flex items-center gap-1.5"
                data-testid="copy-install-command-with-key-btn"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
            <details className="mt-3 text-xs text-gray-500">
              <summary className="cursor-pointer hover:text-gray-300">Or use the generic install command (will prompt for the key)</summary>
              <div className="mt-2 flex items-stretch gap-2">
                <code
                  data-testid="install-command"
                  className="flex-1 px-3 py-2 bg-black/70 border border-gray-700 rounded-md text-gray-200 text-xs font-mono select-all break-all"
                >
                  {installCommandGeneric}
                </code>
                <button
                  onClick={() => copyText(installCommandGeneric, 'Install command copied — paste it in Terminal.')}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-md flex items-center gap-1.5"
                  data-testid="copy-install-command-btn"
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
            </details>
          </>
        ) : (
          <>
            <p className="text-xs text-gray-400 mb-3">
              Open Terminal on your Mac, paste this, follow the prompt to paste your sync key. Sets up auto-sync every 15 minutes via launchd.
            </p>
            <div className="flex items-stretch gap-2">
              <code
                data-testid="install-command"
                className="flex-1 px-3 py-2 bg-black/70 border border-gray-700 rounded-md text-gray-200 text-xs font-mono select-all break-all"
              >
                {installCommandGeneric}
              </code>
              <button
                onClick={() => copyText(installCommandGeneric, 'Install command copied — paste it in Terminal.')}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white text-xs font-semibold rounded-md flex items-center gap-1.5"
                data-testid="copy-install-command-btn"
              >
                <Copy size={14} /> Copy
              </button>
            </div>
          </>
        )}
        <details className="mt-3 text-xs text-gray-400">
          <summary className="cursor-pointer hover:text-gray-200 flex items-center gap-1">
            <Terminal size={12} />
            What this does (and how to undo it)
          </summary>
          <ul className="mt-2 list-disc list-inside space-y-1 text-gray-400">
            <li>Saves your key to <code className="text-amber-300">~/.operatr_sync_key</code> (chmod 600)</li>
            <li>Downloads the sync agent to <code className="text-amber-300">~/.operatr_sync.sh</code></li>
            <li>Writes a launchd plist at <code className="text-amber-300">~/Library/LaunchAgents/com.operatr.outlooksync.plist</code></li>
            <li>Loads the launchd job — runs every 15 min</li>
            <li>Stop with <code className="text-amber-300">launchctl unload ~/Library/LaunchAgents/com.operatr.outlooksync.plist</code></li>
            <li>Watch the log live with <code className="text-amber-300">tail -f ~/.operatr_sync.log</code></li>
          </ul>
        </details>
      </div>

      {/* Active keys list */}
      <div className="bg-gray-800 border border-gray-700 rounded-xl p-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-white font-semibold">Active keys</h4>
          <button
            onClick={loadKeys}
            disabled={loading}
            className="text-xs text-gray-400 hover:text-white flex items-center gap-1 disabled:opacity-50"
            data-testid="refresh-sync-keys-btn"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refresh
          </button>
        </div>
        {keys.length === 0 ? (
          <p className="text-xs text-gray-500">No active keys yet. Generate one above.</p>
        ) : (
          <ul className="space-y-2">
            {keys.map(k => (
              <li
                key={k.id}
                data-testid={`sync-key-row-${k.id}`}
                className="flex items-center gap-3 px-3 py-2 bg-black/40 border border-gray-700 rounded-md"
              >
                <KeyRound size={14} className="text-amber-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{k.label || 'Unnamed device'}</div>
                  <div className="text-[10px] text-gray-500 font-mono">
                    {k.key_prefix}…{k.key_suffix}
                  </div>
                  <div className="text-[10px] text-gray-500">
                    Created {k.created_at ? new Date(k.created_at).toLocaleString() : '—'}
                    {k.last_used_at ? <> · Last used {new Date(k.last_used_at).toLocaleString()}</> : <> · Never used</>}
                    {typeof k.use_count === 'number' ? <> · {k.use_count} syncs</> : null}
                  </div>
                </div>
                <button
                  onClick={() => revokeKey(k.id)}
                  className="px-2 py-1 text-xs text-red-300 hover:text-white hover:bg-red-600/30 border border-red-500/40 rounded flex items-center gap-1 flex-shrink-0"
                  data-testid={`revoke-sync-key-${k.id}`}
                  title="Revoke this key — the Mac using it will stop syncing"
                >
                  <Trash2 size={12} /> Revoke
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PermanentSyncKeyPanel;
